import { collection, getDocs, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ActiveUsersData, UserPresence } from '../types/Presence';

const PRESENCE_COLLECTION = 'userPresence';
const ACTIVE_THRESHOLD_MINUTES = 5; // Consider user active if seen in last 5 minutes

export const presenceService = {
  /**
   * Subscribe to real-time active users updates
   */
  subscribeToActiveUsers(
    callback: (data: ActiveUsersData) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const presenceRef = collection(db, PRESENCE_COLLECTION);

    return onSnapshot(
      presenceRef,
      (snapshot) => {
        const now = new Date();
        const threshold = new Date(now.getTime() - ACTIVE_THRESHOLD_MINUTES * 60 * 1000);
        const activeUsers: UserPresence[] = [];

        snapshot.forEach(doc => {
          const data = doc.data();
          const lastSeenDate = data.lastSeen?.toDate?.() || new Date(data.lastSeen);
          const isOnline = lastSeenDate >= threshold;

          if (isOnline) {
            activeUsers.push({
              userId: data.userId || doc.id,
              userName: data.userName,
              lastSeen: lastSeenDate.toISOString(),
              currentLevel: data.currentLevel,
              isOnline
            });
          }
        });

        callback({
          activeCount: activeUsers.length,
          users: activeUsers,
          lastUpdated: now.toISOString()
        });
      },
      (error) => {
        console.error('Error in active users subscription:', error);
        if (onError) {
          onError(error);
        }
      }
    );
  },

  /**
   * Get count of currently active users (one-time fetch)
   */
  async getActiveUsers(): Promise<ActiveUsersData> {
    try {
      const now = new Date();
      const threshold = new Date(now.getTime() - ACTIVE_THRESHOLD_MINUTES * 60 * 1000);

      const presenceRef = collection(db, PRESENCE_COLLECTION);
      const snapshot = await getDocs(presenceRef);

      const activeUsers: UserPresence[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const lastSeenDate = data.lastSeen?.toDate?.() || new Date(data.lastSeen);

        const isOnline = lastSeenDate >= threshold;

        if (isOnline) {
          activeUsers.push({
            userId: data.userId || doc.id,
            userName: data.userName,
            lastSeen: lastSeenDate.toISOString(),
            currentLevel: data.currentLevel,
            isOnline
          });
        }
      });

      return {
        activeCount: activeUsers.length,
        users: activeUsers,
        lastUpdated: now.toISOString()
      };
    } catch (error) {
      console.error('Error fetching active users:', error);
      return {
        activeCount: 0,
        users: [],
        lastUpdated: new Date().toISOString()
      };
    }
  },

  /**
   * Get all users (active and inactive)
   */
  async getAllUsers(): Promise<UserPresence[]> {
    try {
      const presenceRef = collection(db, PRESENCE_COLLECTION);
      const snapshot = await getDocs(presenceRef);

      const now = new Date();
      const threshold = new Date(now.getTime() - ACTIVE_THRESHOLD_MINUTES * 60 * 1000);

      const users: UserPresence[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const lastSeenDate = data.lastSeen?.toDate?.() || new Date(data.lastSeen);

        users.push({
          userId: data.userId || doc.id,
          userName: data.userName,
          lastSeen: lastSeenDate.toISOString(),
          currentLevel: data.currentLevel,
          isOnline: lastSeenDate >= threshold
        });
      });

      return users.sort((a, b) =>
        new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime()
      );
    } catch (error) {
      console.error('Error fetching all users:', error);
      return [];
    }
  }
};
